import React, {useState} from 'react'
import clinicService from '../../../../../services/clinic.service'
import {toast} from 'react-toastify'
import {useForm} from 'react-hook-form'

function AddLinksForm({clinicid, getLinks, onCancel, existingLinks}) {
    const {register, handleSubmit, formState: {errors}, reset} = useForm()
    const [isSubmitting, setIsSubmitting] = useState(false)

    // Get the platforms that are already added
    const getUsedPlatforms = () => {
        if (!existingLinks || !Array.isArray(existingLinks)) return [];
        return existingLinks.map(link => link.linkType);
    }

    const usedPlatforms = getUsedPlatforms();

    // Platform options with icons
    const platformOptions = [
        {value: 'FACEBOOK', label: 'Facebook', icon: 'bi-facebook'},
        {value: 'WEBSITE', label: 'Website', icon: 'bi-globe'},
        {value: 'INSTAGRAM', label: 'Instagram', icon: 'bi-instagram'},
        {value: 'GOOGLE', label: 'Google', icon: 'bi-google'},
        {value: 'YELP', label: 'Yelp', icon: 'bi-star'}
    ];

    // Filter out already used platforms
    const availablePlatforms = platformOptions.filter(
        platform => !usedPlatforms.includes(platform.value)
    );

    const onSubmit = (data) => {
        setIsSubmitting(true)
        clinicService.addUpdateReputationLink(clinicid, data)
            .then(res => {
                getLinks()
                reset()
                setIsSubmitting(false)
            })
            .catch(err => {
                toast.error("Error adding link. Please try again.", {
                    position: toast.POSITION.BOTTOM_RIGHT
                })
                setIsSubmitting(false)
            })
    }

    return (
        <div className="card card-padding card-border shadow-none mb-4 small">
            <div className="card-title">
                <div className="mb-0">
                    <i className="bi bi-plus-circle me-2"></i>
                    Add New Reputation Link
                </div>
            </div>
            <div className="card-body">
                <form onSubmit={handleSubmit(onSubmit)}>
                    <div className="row">
                        <div className="col-md-6 mb-3">
                            <label className="form-label fw-semibold">
                                <i className="bi bi-tag me-1"></i>Platform
                            </label>
                            <select
                                className={`form-select ${errors.linkType ? 'is-invalid' : ''}`}
                                {...register("linkType", {required: "Platform is required"})}
                                disabled={isSubmitting || availablePlatforms.length === 0}
                            >
                                <option value="">Select a platform</option>
                                {availablePlatforms.map(platform => (
                                    <option key={platform.value} value={platform.value}>
                                        <i className={`bi ${platform.icon} me-2`}></i>
                                        {platform.label}
                                    </option>
                                ))}
                            </select>
                            {errors.linkType && (
                                <div className="invalid-feedback">
                                    {errors.linkType.message}
                                </div>
                            )}
                            {availablePlatforms.length === 0 && (
                                <div className="form-text text-warning">
                                    <i className="bi bi-exclamation-triangle me-1"></i>
                                    All available platforms have been added.
                                </div>
                            )}
                        </div>

                        <div className="col-md-6 mb-3">
                            <label className="form-label fw-semibold">
                                <i className="bi bi-link-45deg me-1"></i>URL
                            </label>
                            <input
                                type="text"
                                className={`form-control ${errors.link ? 'is-invalid' : ''}`}
                                placeholder="https://example.com"
                                disabled={isSubmitting}
                                {...register("link", {
                                    required: "URL is required",
                                    pattern: {
                                        value: /^(https?:\/\/)/,
                                        message: "URL must start with http:// or https://"
                                    }
                                })}
                            />
                            {errors.link && (
                                <div className="invalid-feedback">
                                    {errors.link.message}
                                </div>
                            )}
                            <input type="hidden" {...register("clinicId")} value={clinicid}/>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-md-6 mb-3">
                            <label className="form-label fw-semibold">
                                <i className="bi bi-chat-text me-1"></i>Notes
                            </label>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Optional notes about this link"
                                disabled={isSubmitting}
                                {...register("notes")}
                            />
                        </div>

                        <div className="col-md-6 mb-3">
                            <label className="form-label fw-semibold">
                                <i className="bi bi-plus-circle-dotted me-1"></i>Extra Data
                            </label>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Any additional data"
                                disabled={isSubmitting}
                                {...register("extraData")}
                            />
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-md-6 mb-3">
                            <label className="form-label fw-semibold">
                                <i className="bi bi-toggle-on me-1"></i>Status
                            </label>
                            <select
                                className="form-select"
                                disabled={isSubmitting}
                                {...register("isActive")}
                            >
                                <option value="true">Active</option>
                                <option value="false">Inactive</option>
                            </select>
                        </div>
                    </div>

                    <div className="d-flex justify-content-end gap-2 pt-2">
                        {onCancel && (
                            <button
                                type="button"
                                className="btn btn-danger btn-sm"
                                onClick={onCancel}
                                disabled={isSubmitting}
                            >
                                <i className="bi bi-x-circle me-1"></i>
                                Cancel
                            </button>
                        )}
                        <button
                            type="submit"
                            className="btn btn-success btn-sm d-flex align-items-center"
                            disabled={isSubmitting || availablePlatforms.length === 0}
                        >
                            {isSubmitting ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status"
                                          aria-hidden="true"></span>
                                    Saving...
                                </>
                            ) : (
                                <>
                                    <i className="bi bi-floppy me-1"></i>
                                    Save
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    )
}

export default AddLinksForm